import React, { useState, useEffect, useImperativeHandle, useCallback } from 'react';
import { connect } from 'react-redux';
import { InputSearch } from '../../../../components';
import { isNullOrEmptyArray } from '../../../../util/arrays';
import { removerCaracteresEspeciais } from '../../../../util/formatters/stringNormalizer';
import Scores from '../../../Scores';
import SingleQuestionAwareCollapsibleList from '../single';
import { adaptQuestionsToData } from './adapters';

import * as Ui from './styles';

function MultiQuestionsAwareCollapsibleList({
  /**
   * identificador do cliente selecionado atualmente
   */
  customerId,
  /**
   * identificador da pesquisa selecionada atualmente
   */
  surveyId,
  /**
   * perguntas da pesquisa selecionada na estrutura de dados [{id, enunciado}],
   * elasa vem automaticamente da store
   */
  questions = [],
  /**
   * Controle externo de estado, sao as perguntas que
   * devem ser marcadas automaticamente no seletor
   */
  initialSelectedQuestionsIds = [/* numbers (ids) */],
  /**
   * relação de alternativas selecionadas para cada pergunta
   */
  initialSelectedAlternativesIds = [/* questionId, answerId */],
  /**
   * informa o score (nota de corte inicial)
   */
  initialScore = 0.1,
  /**
   * define o label usado para este componente como um todo
   */
  label,
  /**
   * conteúdo html ou component para ser renderizado como erro
   */
  errorComponent,
  /**
   * placeholder do input de pesquisa do selector
   */
  inputPlaceholder = 'Busque pelo enunciado',
  /**
   * define se o componente expandirá para recuperar alternativas
   */
  expansibleItems = false,
  /**
   * define se o componente pode ter seus itens selecionados ou não
   */
  selectableItems = false,
  /**
   * apresenta nota de corte
   */
  showScore = false,
  /**
   * propaga o evento de alteração na listagem de questões selecionadas
   */
  onSelectQuestions,
  /**
   * propaga o evento de seleção de alternativas
   */
  onChangeSelectedAlternativesIds,
  /**
   * propaga o evento de seleção de uma nota de corte
   */
  onChangeMinMaxRange,
  /**
   * Perguntas a serem ignoradas na listagem [1,2,3...]
   */
  questionsToIgnore = [],

  /** 
   * Controla a exibição do checkbox de perguntas na configuração de um cruzamento
   */
  hiddenCheckboxCollapsibleQuestion
  /**
   * outras propriedades
   */
  // ...props
}, ref) {
  // timeout for load text in input
  let timeoutId = 0;

  // states
  const [internalSelectedIds, setInternalSelectedIds] = useState([]);
  const [initialIgnoredIds, setInitialIgnoredIds] = useState([]);
  const [questionsData, setQuestionsData] = useState([]);
  const [searchText, setSearchText] = useState('');

  // components wrapper
  const ErrorComponentWrapped = () => (errorComponent ? (<>{errorComponent}</>) : null);

  // callbacks: initialize datas
  const initialize = useCallback((allQuestions, ignoredIds, filterText) => {
    // filter by text
    function filter(data, text) {
      if (!text) return data;
      const lowerCaseText = text.toLowerCase();
      const filterResult = data.filter((item) => removerCaracteresEspeciais(item.label)
        .toLowerCase().includes(lowerCaseText));
      return filterResult;
    }
    // helper for build questions data
    function prepareNewQuestions() {
      // adapt questions for this component
      const newData = adaptQuestionsToData(
        [...allQuestions], internalSelectedIds,
      );
      // remote quesions by questionToIgnore
      ignoredIds.forEach((id) => {
        const indexToRemove = newData.findIndex((q) => q.value === id);
        newData.splice(indexToRemove, 1);
      });
      // set question data
      const sel = filter(newData, filterText);
      setQuestionsData(sel);
    }
    // prepare customs questions
    if (!isNullOrEmptyArray(ignoredIds) || !isNullOrEmptyArray(allQuestions)) {
      prepareNewQuestions();
    }
  }, [internalSelectedIds]);

  // effect from initialSelectedQuestionsIds update
  useEffect(() => {
    if (!isNullOrEmptyArray(initialSelectedQuestionsIds)) {
      setInternalSelectedIds(initialSelectedQuestionsIds);
    }
  }, [initialSelectedQuestionsIds]);

  // effect from questionsToIgnore update
  useEffect(() => {
    if (!isNullOrEmptyArray(questionsToIgnore)) {
      setInitialIgnoredIds(questionsToIgnore);
    }
  }, [questionsToIgnore]);

  // effect of update questions
  useEffect(() => {
    initialize(questions, initialIgnoredIds, searchText);
  }, [initialize, questions, initialIgnoredIds, searchText]);

  // api para este componente poder
  // ser acesso pelo parent
  // on reset datas
  useImperativeHandle(ref, () => ({
    reset: () => {
      // reset search text
      setSearchText('');
      // initialize
      initialize(questions, questionsToIgnore, '');
    },
  }));

  // handle change search text
  function handleChangeTextSearch(value) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      setSearchText(value);
    }, 300);
  }

  // handle on select item
  function handleSelectQuestions(checked, questionId) {
    if (checked) {
      const sel = [...internalSelectedIds, questionId];
      setInternalSelectedIds(sel);
      if (onSelectQuestions) onSelectQuestions(sel);
    } else {
      const sel = internalSelectedIds.filter((id) => id !== questionId);
      setInternalSelectedIds(sel);
      if (onSelectQuestions) onSelectQuestions(sel);
    }
  }

  function handleChangeMinMaxRange(values) {
    if (onChangeMinMaxRange) onChangeMinMaxRange(values);
  }

  function handleChangeSelectedAlternativesIds(qId, altsIds) {
    if (onChangeSelectedAlternativesIds) onChangeSelectedAlternativesIds(qId, altsIds);
  }

  function selectedAlts(quest) {
    const result = [];
    initialSelectedAlternativesIds.forEach((a) => {
      if (a.questionId === quest.value) {
        result.push(a.answerId);
      }
    });
    return result;
  }

  return (
    <Ui.Container className="MultiQuestionsAwareCollapsibleList">
      <Ui.HeadSection className="Ui.HeadSection">
        {/* filtro */}
        <Ui.FilterSectionItem>
          <Ui.Label>{label}</Ui.Label>
          <InputSearch
            placeholder={inputPlaceholder}
            styleClass="inputsearch"
            onChangeText={(value) => handleChangeTextSearch(value)}
          />
        </Ui.FilterSectionItem>
        {/* search */}
        {showScore ? (
          <Ui.CutSectionItem className="Ui.CutSectionItem">
            <Scores
              dark
              initialScore={initialScore}
              onChange={(values) => handleChangeMinMaxRange(values)}
            />
          </Ui.CutSectionItem>
        ) : null}
      </Ui.HeadSection>
      {/* error */}
      <Ui.ErrorSection>
        <ErrorComponentWrapped />
      </Ui.ErrorSection>
      {/* list */}
      <Ui.BodySection className="Ui.BodySection collapsible-body">
        {questionsData.map((question) => (
          <Ui.CollapsibleListWrapper key={`key-${question.value}`} className="Ui.CollapsibleListWrapper">
            <SingleQuestionAwareCollapsibleList
              className="SingleQuestionAwareCollapsibleList"
              selectableItem={selectableItems}
              customerId={customerId}
              surveyId={surveyId}
              questionId={question.value}
              questionTitle={question.label}
              checkedTitle={question.checked}
              selectedItems={selectedAlts(question)}
              expansible={expansibleItems}
              onSelectQuestion={(checked, questionId) => handleSelectQuestions(checked, questionId)}
              onChangeSelectedAlternativesIds={(questionId, altsIds) => handleChangeSelectedAlternativesIds(questionId, altsIds)}
              hiddenCheckboxCollapsibleQuestion={hiddenCheckboxCollapsibleQuestion}
            />
          </Ui.CollapsibleListWrapper>
        ))}
      </Ui.BodySection>
    </Ui.Container>
  );
}


/**
 * recria a listagem de alternativas de acordo com o estado do mapa alternativas
 *
 * @param {*} state
 * @param {*} props
 */
const mapStateToSelectedSurveyQuestions = (customerId, surveyId, surveys, alternativesState) => {
  // validation
  if (!customerId || !surveyId) {
    return [];
  }
  // get questions
  const surveysArr = surveys.filter((s) => s.id === surveyId);
  if (isNullOrEmptyArray(surveysArr)) {
    return [];
  }
  // recreate questions
  const questionsV0 = [...surveysArr[0].perguntas];
  const questionsV1 = questionsV0.map((q) => {
    // new question
    const nq = { ...q };
    // state key
    const key = { customerId, surveyId, questionId: q.id };
    // get alternative state by key
    const a = alternativesState.get(key);
    // if exists
    nq.running = a ? a.running : false;
    nq.error = a ? a.error : null;
    nq.alternativas = a ? a.payload : [];
    // result
    return nq;
  });
  //
  return questionsV1;
};

/**
 * mapeia a store para as propriedades que serão passadas para este
 * componente.
 *
 * Duas propriedades são extraídas e passadas para o componente:
 * questions => obtido da pesquisa selecionada
 *
 * @param {*} state
 * @param {*} props
 */
const mapStateToProps = (state, props) => {
  // get props
  const { customerId, surveyId } = props;
  // states
  const surveyStatePayload = state.surveys.payload;
  const alternativesState = state.alternatives;
  // create questions (local)
  const questions = mapStateToSelectedSurveyQuestions(
    customerId,
    surveyId,
    surveyStatePayload,
    alternativesState,
  );
  // result
  return {
    questions,
  };
};

// create component (delegate) and export
export default connect(
  mapStateToProps, null, null, { forwardRef: true },
)(
  React.forwardRef(MultiQuestionsAwareCollapsibleList),
);
